import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { roomColumns } from './RoomStatusData';
import DataTable from '../../../../../../Common/DataTable/DataTable';
import DataTableHeader from '../../../../../../Common/DataTableHeader/DataTableHeader';
import DataTableFooter from '../../../../../../Common/DataTableFooter/DataTableFooter';
import EditRoomModal from './EditRoomModal';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentPage } from '../../../../../../Redux/slice/features/paginationSilce';
import { Button } from 'react-bootstrap';
import { FaPencilAlt, FaTrash } from 'react-icons/fa';
import { REACT_APP_API_URL } from '../../../../../../env';

const RoomStatusTable = () => {
  const [rooms, setRooms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const dispatch = useDispatch();
  const currentPage = useSelector((state) => state.pagination.currentPage);
  const itemsPerPage = useSelector((state) => state.pagination.itemsPerPage);

  const fetchRoomData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${REACT_APP_API_URL}api/room-config/get-room-configs`);
      const data = res.data.data || [];
      setRooms(data);
    } catch (err) {
      console.error('Error fetching room data:', err);
      setError('Failed to load room data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    dispatch(setCurrentPage(1));
    fetchRoomData();
  }, [dispatch, fetchRoomData]);

  const handleEditClick = useCallback((roomId) => {
    setSelectedRoomId(roomId);
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(async (roomId) => {
    try {
      await axios.delete(`${REACT_APP_API_URL}api/room-config/delete-room/${roomId}`);
      setRooms((prev) => prev.filter((r) => r.HotelRoomConfigID !== roomId));
      alert('Room deleted successfully!');
    } catch (err) {
      console.error('Error deleting room:', err);
      alert('Failed to delete room');
    }
  }, []);

  const formattedRooms = useMemo(() => {
    return rooms.map((room, index) => ({
      Sl: index + 1,
      hotelRoomID: room.HotelRoomConfigID,
      roomType: room.RoomType || 'N/A',
      description: room.Description || '',
      actions: (
        <>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => handleEditClick(room.HotelRoomConfigID)}
          >
            <FaPencilAlt />
          </Button>{' '}
        
        </>
      ),
    }));
  }, [rooms, handleEditClick, handleDelete]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = useMemo(() => formattedRooms.slice(indexOfFirstItem, indexOfLastItem), [
    formattedRooms,
    indexOfFirstItem,
    indexOfLastItem,
  ]);

  const handleNextPage = useCallback(() => {
    if (currentPage < Math.ceil(formattedRooms.length / itemsPerPage)) {
      dispatch(setCurrentPage(currentPage + 1));
    }
  }, [currentPage, formattedRooms.length, itemsPerPage, dispatch]);

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      dispatch(setCurrentPage(currentPage - 1));
    }
  }, [currentPage, dispatch]);

  return (
    <>
      {loading && <h4>Loading...</h4>}
      {error && <p className="text-danger">Error: {error}</p>}

      <div className="card">
        <div className="card-body">
          <DataTableHeader columns={roomColumns} data={formattedRooms} />
          <DataTable columns={roomColumns} data={currentItems} enablePagination={false} />
          {formattedRooms.length > 0 && (
            <DataTableFooter
              dataT={formattedRooms}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              handleNextPage={handleNextPage}
              handlePreviousPage={handlePreviousPage}
            />
          )}
        </div>
      </div>

      {showModal && (
        <EditRoomModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedRoomId(null);
          }}
          
          roomId={selectedRoomId}
          onUpdated={fetchRoomData}
        />
      )}
    </>
  );
};

export default React.memo(RoomStatusTable);